import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { Alert, Button, Container } from 'react-bootstrap';
import { useAuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

const SubscriptionBanner = () => {
  const { authState, loggedInUser } = useAuthContext();
  const navigate = useNavigate();
  const bannerRef = useRef(null);
  const { subscription, subscriptionStatus, isFetchingSubscription, isAuthenticated } = authState;
  const effectiveStatus = subscription?.status || subscriptionStatus;
  const isLegalRepresentative = loggedInUser?.role_key === 'representante_legal';

  const banner = useMemo(() => {
    if (!isAuthenticated || (isFetchingSubscription && !subscription)) return null;

    if (effectiveStatus === 'past_due') {
      const expired = subscription?.payment_grace_expired || subscription?.access_mode === 'consultorio_only';
      const remaining = subscription?.grace_days_remaining ?? 0;

      return {
        variant: expired ? 'danger' : 'warning',
        message: expired
          ? (isLegalRepresentative
            ? 'O prazo de tolerância terminou. O acesso está limitado ao Consultório até a regularização da assinatura.'
            : 'O acesso está limitado ao Consultório. Solicite ao Responsável que verifique o plano de assinatura.')
          : (isLegalRepresentative
            ? `A renovação da assinatura está pendente. Restam ${remaining} dia(s) de tolerância para regularizar o pagamento.`
            : 'A renovação da assinatura está pendente e a clínica está no período de tolerância. Informe ao Responsável que o sistema poderá ter as funcionalidades reduzidas.'),
      };
    }

    if (['canceled', 'unpaid'].includes(effectiveStatus)) {
      return {
        variant: 'danger',
        message: isLegalRepresentative
          ? 'A assinatura não está ativa. Regularize o plano para restabelecer o acesso.'
          : 'A assinatura da clínica não está ativa. Solicite ao Responsável que verifique o plano.',
      };
    }

    if (subscription?.plan && !subscription.plan.is_lifetime && subscription.current_period_ends_at) {
      const expirationDate = new Date(`${subscription.current_period_ends_at}T00:00:00`);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diffDays = Math.ceil((expirationDate.getTime() - today.getTime()) / 86400000);

      if (diffDays < 0) {
        return {
          variant: 'danger',
          message: `Seu plano expirou há ${Math.abs(diffDays)} dia(s). Verifique a situação da assinatura.`,
        };
      }

      if (diffDays <= 5 && ['Plano de Teste', 'Plano Essencial'].includes(subscription.plan?.name)) {
        return {
          variant: 'warning',
          message: `Seu plano expira em ${diffDays} dia(s). Garanta o acesso contínuo à plataforma.`,
        };
      }
    }

    return null;
  }, [isAuthenticated, isFetchingSubscription, subscription, effectiveStatus, isLegalRepresentative]);

  useLayoutEffect(() => {
    const root = document.documentElement;
    const element = bannerRef.current;

    if (!banner || !element) {
      root.style.removeProperty('--subscription-banner-height');
      return undefined;
    }

    const updateHeight = () => {
      root.style.setProperty('--subscription-banner-height', `${element.offsetHeight}px`);
    };
    updateHeight();

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateHeight) : null;
    observer?.observe(element);
    window.addEventListener('resize', updateHeight);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateHeight);
      root.style.removeProperty('--subscription-banner-height');
    };
  }, [banner]);
  if (!banner) return null;

  const Icon = banner.variant === 'danger' ? faExclamationTriangle : faInfoCircle;
  const canRegularize = isLegalRepresentative && ['past_due', 'canceled', 'unpaid'].includes(effectiveStatus);

  return (
    <Alert ref={bannerRef} variant={banner.variant} className="text-center mb-0 rounded-0 py-2" style={{ position: 'sticky', top: 0, zIndex: 1051 }}>
      <Container className="d-flex justify-content-center align-items-center flex-wrap gap-2">
        <FontAwesomeIcon icon={Icon} />
        <span>{banner.message}</span>
        {canRegularize && (
          <Button variant={`outline-${banner.variant}`} size="sm" onClick={() => navigate('/pricing')}>
            Verificar assinatura
          </Button>
        )}
      </Container>
    </Alert>
  );
};

export default SubscriptionBanner;